import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/Panel";
import { Combobox } from "@/components/Combobox";
import { descargarPdf } from "@/pages/TrabajosActivos";
import { Wrench, FileDown } from "lucide-react";

const fmtMiles = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("es-CR"));

const fmtFechaHora = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("es-CR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const primerDiaMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};

export default function ReporteMecanicos() {
  const hoy = new Date().toLocaleDateString("en-CA");
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoy);
  const [empleadoId, setEmpleadoId] = useState("");
  const [empleados, setEmpleados] = useState([]);
  const [grupos, setGrupos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    api.get("/empleados").then(({ data }) => setEmpleados(data)).catch(() => {});
  }, []);

  const consultar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde, hasta };
      if (empleadoId) params.empleado_id = empleadoId;
      const { data } = await api.get("/reportes/por-mecanico", { params });
      setGrupos(data);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  }, [desde, hasta, empleadoId]);

  useEffect(() => { consultar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const descargarExcel = () => {
    const params = new URLSearchParams({ desde, hasta });
    if (empleadoId) params.set("empleado_id", empleadoId);
    descargarPdf(`/reportes/por-mecanico.xlsx?${params.toString()}`, `trabajos-por-mecanico_${desde}_${hasta}.xlsx`);
  };

  const opcionesEmpleado = [{ value: "", label: "Todos" }, ...empleados.map((e) => ({
    value: e.id, label: [e.nombre, e.primer_apellido, e.segundo_apellido].filter(Boolean).join(" "),
  }))];

  return (
    <div data-testid="reporte-mecanicos-page" className="space-y-4">
      <Panel titulo="Trabajos por mecánico" testId="reporte-mecanicos-panel">
        <div className="flex flex-col md:flex-row gap-3 md:items-end mb-5">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Desde</label>
            <Input data-testid="reporte-mecanicos-desde-input" type="date" value={desde}
              onChange={(e) => setDesde(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Hasta</label>
            <Input data-testid="reporte-mecanicos-hasta-input" type="date" value={hasta}
              onChange={(e) => setHasta(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1 w-64">
            <label className="text-xs font-medium text-slate-500">Mecánico</label>
            <Combobox testId="reporte-mecanicos-empleado-combobox" value={empleadoId}
              onChange={setEmpleadoId} placeholder="Todos"
              options={opcionesEmpleado} searchPlaceholder="Buscar mecánico..." />
          </div>
          <Button data-testid="reporte-mecanicos-consultar-btn" onClick={consultar} disabled={cargando}>
            {cargando ? "Consultando..." : "Consultar"}
          </Button>
          <Button data-testid="reporte-mecanicos-descargar-excel-btn" variant="outline"
            onClick={descargarExcel} disabled={cargando || !grupos?.length} className="gap-1.5">
            <FileDown size={14} /> Descargar Excel
          </Button>
        </div>

        {cargando && (
          <p data-testid="reporte-mecanicos-cargando" className="text-sm text-slate-500 py-6 text-center">Cargando…</p>
        )}
        {!cargando && grupos && grupos.length === 0 && (
          <p data-testid="reporte-mecanicos-vacio" className="text-sm text-slate-400 py-8 text-center">
            No hay trabajos finalizados en ese rango
          </p>
        )}
        {!cargando && grupos && grupos.length > 0 && (
          <div className="space-y-6">
            {grupos.map((g) => (
              <div key={g.empleado_id} data-testid={`reporte-mecanico-grupo-${g.empleado_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Wrench size={15} className="text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-800">{g.mecanico}</h3>
                  <span data-testid={`reporte-mecanico-total-${g.empleado_id}`}
                    className="text-xs text-slate-500">({g.total_trabajos} trabajo{g.total_trabajos === 1 ? "" : "s"})</span>
                </div>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 bg-slate-50">
                        <th className="py-2 px-3 font-semibold">N.º trabajo</th>
                        <th className="py-2 px-3 font-semibold">Fecha finalización</th>
                        <th className="py-2 px-3 font-semibold">Kilometraje</th>
                        <th className="py-2 px-3 font-semibold">Modelo</th>
                        <th className="py-2 px-3 font-semibold">Placa</th>
                        <th className="py-2 px-3 font-semibold">Servicios realizados</th>
                        <th className="py-2 px-3 font-semibold">Repuestos cambiados</th>
                        <th className="py-2 px-3 font-semibold">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.trabajos.map((t) => (
                        <tr key={t.numero} data-testid={`reporte-mecanico-fila-${t.numero}`}
                          className="border-b border-slate-100 align-top">
                          <td className="py-2 px-3 font-medium text-indigo-700">{t.numero}</td>
                          <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{fmtFechaHora(t.fecha_finalizacion)}</td>
                          <td className="py-2 px-3">{t.kilometraje !== null && t.kilometraje !== undefined ? `${fmtMiles(t.kilometraje)} km` : "—"}</td>
                          <td className="py-2 px-3">{t.modelo || "—"}</td>
                          <td className="py-2 px-3">{t.placa || "—"}</td>
                          <td className="py-2 px-3">
                            {t.servicios.length === 0 ? "—" : (
                              <ul className="space-y-0.5">
                                {t.servicios.map((s, i) => (
                                  <li key={i}>
                                    {s.nombre}
                                    {s.nota && <span className="block text-xs text-slate-400">{s.nota}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {t.repuestos.length === 0 ? "—" : (
                              <ul className="space-y-0.5">
                                {t.repuestos.map((r, i) => (
                                  <li key={i}>
                                    {r.cantidad} × {r.codigo_parte ? `${r.codigo_parte} ` : ""}{r.nombre}
                                    {r.nota && <span className="block text-xs text-slate-400">{r.nota}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="py-2 px-3 text-slate-600">{t.observaciones || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
